import { useMemo } from 'react';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { useCheckFeatureFunction } from '../../../utils/hooks';
import { queryTypeConfigLookup } from '../../../utils/queryTypeLookup';
import { ISidebarLink } from '../../../path';
import { ITooltipOption } from '../../../components/KubitTooltip/globalTooltips';
import { Entries } from '../../../utils/types';

type IReportNavQueryType = Exclude<QueryType, QueryType.CohortSample>;

const reportNavTooltipByQueryType: Record<IReportNavQueryType, ITooltipOption> =
  {
    [QueryType.Expression]: {
      content:
        'Analyze your data by segmenting and filtering events and user counts',
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/query',
    },
    [QueryType.Funnel]: {
      content:
        'Analyze user progression from initial interation to a desired outcome',
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/funnel',
    },
    [QueryType.Path]: {
      content:
        'Create a step-by-step flow diagram for granular user pathing analysis.',
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/path',
    },
    [QueryType.Retention]: {
      content: `Retention shows how different cohorts of users retain over time.
    This is used to optimize user journeys and increase stickiness.`,
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/retention',
    },
    [QueryType.Prediction]: {
      content: `Prediction assesses the likelihood of users transitioning from a source cohort to a target cohort.`,
    },
    [QueryType.DataTable]: {
      content: `Data Tables allow for tabular reporting of metrics and segments`,
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/data-table',
    },
    [QueryType.Significance]: {
      content: `Visualize the statistical significance of differences between a control and variant group(s).`,
      actionText: 'Learn More',
      actionUrl: 'https://docs.kubit.ai/docs/significance-report',
    },
  };

export const useReportNavLinks = (): ISidebarLink[] => {
  const checkFeatureFunc = useCheckFeatureFunction();

  return useMemo(() => {
    return (
      Object.entries(reportNavTooltipByQueryType) as Entries<
        typeof reportNavTooltipByQueryType
      >
    )
      .filter(([key]) => {
        const { requiredFeature } = queryTypeConfigLookup[key];

        if (requiredFeature) {
          return checkFeatureFunc(requiredFeature);
        }

        return true;
      })
      .map(([key, tooltip]) => {
        const { display, url, icon } = queryTypeConfigLookup[key];

        return {
          url,
          title: display,
          icon,
          tooltip,
        } satisfies ISidebarLink;
      });
  }, [checkFeatureFunc]);
};
